import { current, produce } from 'immer'

import {
  extract_author,
  extract_end,
  extract_log,
  extract_message,
  extract_next_steps,
  extract_prompts,
} from './stream.handler'

import {
  ExplainBusinessRuleResponse,
  GenerateBusinessRuleFromDocumentResponse,
  GenerateBusinessRuleResponse,
} from '../../../agents/developer/api/developer.api'
import { AiDeveloperActions } from '../../../agents/developer/store/developer.actions'
import { MessageType } from '../../../common/ai.dictionary'
import { AiState } from '../../ai.state'

export const onExtractBusinessProcessFromDocumentSuccess = (
  state: AiState,
  { response }: { response: GenerateBusinessRuleFromDocumentResponse }
): AiState =>
  produce(state, (draft: AiState) => {
    draft.messages.push({
      message: `<b>Look what I found in the selected document:</b> <p> <i>${response.response}</i> </p> I will use this information to build/modify the bussines process for you.`,
      type: MessageType.AiMsg,
      commited: true,
    })
    AiDeveloperActions.generateBusinessRule({ prompt: response.response })
  })

export const onGenerateBusinessRuleSuccess = (
  state: AiState,
  { response }: { response: GenerateBusinessRuleResponse }
): AiState =>
  produce(state, (draft: AiState) => {
    if (response.chat) {
      draft.messages.push({
        message: response.chat,
        type: MessageType.AiMsg,
        commited: true,
      })
    } else {
      draft.generatedBusinessRule = response
      draft.messages.push({
        message:
          'Business Rule has been generated. You can tweak the results by telling me what to change or edit the preview directly before saving.',
        context: {
          request_id: response.request_id,
        },
        type: MessageType.AiMsg,
        commited: true,
      })
    }
  })

export const onExplainBusinessRuleSuccess = (
  state: AiState,
  { response }: { response: ExplainBusinessRuleResponse }
): AiState =>
  produce(state, (draft: AiState) => {
    if (response.chat) {
      draft.messages.push({
        message: response.chat,
        type: MessageType.AiMsg,
        commited: true,
      })
    } else {
      draft.messages.push({
        message: response.answer,
        context: {
          request_id: response.request_id,
        },
        type: MessageType.AiMsg,
        commited: true,
      })
    }
  })

export const onImproveCodeSuccess = (state: AiState, { response }: { response: any }): AiState =>
  produce(state, (draft) => {
    const improvedCode = response.answer.split('\n')
    const originalCode = state.codeSelectionContext.code.split('\n')
    const resultedCode = [
      ...originalCode.slice(0, state.codeSelectionContext.startLineNumber - 1),
      ...improvedCode,
      ...originalCode.slice(state.codeSelectionContext.endLineNumber),
    ]
    draft.codeSelectionContext.selected = response.answer
    draft.codeSelectionContext.endLineNumber =
      state.codeSelectionContext.startLineNumber + improvedCode.length - 1
    draft.generatedImprovedCode = resultedCode.join('\n')
    draft.messages.push({
      message: response.explanation,
      context: {
        request_id: response.request_id,
      },
      type: MessageType.AiMsg,
      commited: true,
    })
  })

export const onAddCommentSuccess = (state: AiState, { response }: { response: any }): AiState =>
  produce(state, (draft) => {
    const improvedCode = response.answer.split('\n')
    const originalCode = state.codeSelectionContext.code.split('\n')
    const resultedCode = [
      ...originalCode.slice(0, state.codeSelectionContext.startLineNumber - 1),
      ...improvedCode,
      ...originalCode.slice(state.codeSelectionContext.endLineNumber),
    ]
    draft.codeSelectionContext.selected = response.answer
    draft.codeSelectionContext.endLineNumber =
      state.codeSelectionContext.startLineNumber + improvedCode.length - 1
    draft.generatedAddComment = resultedCode.join('\n')
    draft.messages.push({
      message: `Comments added, check the code editor`,
      context: {
        request_id: response.request_id,
      },
      type: MessageType.AiMsg,
      commited: true,
    })
  })

export const onFixBusinessRuleWarningsSuccess = (
  state: AiState,
  { response }: { response: any }
): AiState =>
  produce(state, (draft: AiState) => {
    draft.generatedFixWarningsBusinessRule = response
    draft.messages.push({
      message: response.answer,
      context: {
        request_id: response.request_id,
      },
      type: MessageType.AiMsg,
      commited: true,
    })
  })

export const onFixBusinessRuleErrorSuccess = (
  state: AiState,
  { response }: { response: any }
): AiState =>
  produce(state, (draft) => {
    draft.generatedFixErrorBusinessRule = response
    draft.messages.push({
      message: response.answer,
      context: {
        request_id: response.request_id,
      },
      type: MessageType.AiMsg,
      commited: true,
    })
  })

export const onGenerateExpressionSuccess = (
  state: AiState,
  { response }: { response: any }
): AiState =>
  produce(state, (draft: AiState) => {
    if (response.chat) {
      draft.messages.push({
        message: response.chat,
        type: MessageType.AiMsg,
        commited: true,
      })
    } else {
      draft.generatedExpression = response
      draft.messages.push({
        message: 'Expression has been generated.',
        context: {
          request_id: response.request_id,
        },
        type: MessageType.AiMsg,
        commited: true,
      })
    }
  })

export const onGenerateGatewayConditionSuccess = (
  state: AiState,
  { response }: { response: any }
): AiState =>
  produce(state, (draft: AiState) => {
    draft.generatedGatewayCondition = response
    draft.messages.push({
      message:
        'Gateway condition expressions have been generated. Review the conditions and click Keep to apply them.',
      context: {
        request_id: response.request_id,
      },
      type: MessageType.AiMsg,
      commited: true,
    })
  })

export const onGetAnswerEventStream = (state: AiState, { response }: { response: any }): AiState =>
  produce(state, (draft: AiState) => {
    if (!current(draft).backgroundAiResponse) {
      draft.streamingAiResponse = true
      draft.pendingAiResponse = true
    }
    const { message, context, sources } = response
    const lastMessageDraft = draft.messages[draft.messages.length - 1]
    const answer = extract_message(message)
    const nextSteps = extract_next_steps(message)
    const end = extract_end(message)
    const log = extract_log(message)
    const prompts = extract_prompts(message)
    const author = extract_author(message)

    if (message.answer?.operation === 'session') {
      draft.sessionId = message.answer.thread_id
      if (message.answer.request_id) {
        draft.lastRequestId = message.answer.request_id
      }
    }
    // parse logs and register
    if (log) {
      draft.logs.unshift({
        agent: log.agent,
        message: log.debug.info,
        message_type: 'INFO',
        timestamp: log.timestamp,
        status: log.debug.status,
        step: log.debug.step,
      })
      draft.currentAITaskMessage = log.debug.info
      return
    }
    if ((message as any).answer) {
      parseAnswerMessage(draft, message)
    }
    if (end) {
      parseEndMessage(draft, message)
      return
    }
    if (!answer && !nextSteps) {
      return
    }
    const isLastMessageOfTypeAi =
      lastMessageDraft &&
      lastMessageDraft?.type === MessageType.AiMsg &&
      !lastMessageDraft?.commited
    if (isLastMessageOfTypeAi) {
      Object.assign(lastMessageDraft, response)
      lastMessageDraft.message = nextSteps + answer
      lastMessageDraft.context = null
      lastMessageDraft.sources = sources
      lastMessageDraft.author = author
      lastMessageDraft.html_preview = null
      lastMessageDraft.prompts = prompts
      lastMessageDraft.type = MessageType.AiMsg
    } else {
      draft.messages.push({
        ...response,
        message,
        context,
        sources,
        type: MessageType.AiMsg,
      })
    }
  })

function parseAnswerMessage(draft: AiState, messagePayload): void {
  const payload_code = messagePayload.end
    ? { business_rule: messagePayload.end.answer }
    : messagePayload.answer
  generateBusinessRule(draft, payload_code)
  const payload_test_data =
    messagePayload.end && messagePayload.end.operation === 'test_data'
      ? { test_data: messagePayload.end.answer }
      : messagePayload.answer
  generateTestData(draft, payload_test_data)
  // End-event shape is flat (`end.answer = {jsx, css, packageJson, ...}`);
  // intermediate shape is nested (`answer = {custom_component: {jsx, ...}}`).
  // Wrap the flat end-shape so the downstream `.custom_component` lookup
  // matches both — mirrors the `business_rule` pattern above.
  const payload_custom_component =
    messagePayload.end && messagePayload.end.operation === 'custom_component'
      ? { custom_component: messagePayload.end.answer }
      : messagePayload.answer
  generateCustomComponentCode(draft, payload_custom_component)
}

function generateBusinessRule(draft: AiState, messagePayload): void {
  if (messagePayload && messagePayload.business_rule) {
    draft.generatedBusinessRule = {
      answer: messagePayload.business_rule,
    } as any
  }
  if (messagePayload && messagePayload.reusable_function) {
    draft.generatedBusinessRule = {
      answer: messagePayload.reusable_function,
    } as any
  }
}

function generateTestData(draft: AiState, messagePayload): void {
  if (messagePayload && messagePayload.test_data) {
    draft.generatedTestData = JSON.stringify(messagePayload.test_data, undefined, 2)
  }
}

function generateCustomComponentCode(draft: AiState, messagePayload): void {
  if (messagePayload?.custom_component) {
    draft.generatedCustomComponent = {
      sourceCode:
        messagePayload.custom_component.jsx ?? messagePayload.custom_component.sourceCode ?? null,
      styles: messagePayload.custom_component.css ?? messagePayload.custom_component.styles ?? null,
      packageJson: messagePayload.custom_component.packageJson ?? null,
    }
  }
}

function parseEndMessage(draft: AiState, message): void {
  const messagePayload = message.end

  if (messagePayload.operation === 'business_rule') {
    parseAnswerMessage(draft, message)
    draft.reasoningLogs.unshift({
      agent: message.agent,
      message: message.end.answer.reasoning,
      message_type: 'REASONING',
      operation: 'BUSINESS RULE GENERATION',
    })
  }
  if (messagePayload.operation === 'reusable_function') {
    parseAnswerMessage(draft, message)
    draft.reasoningLogs.unshift({
      agent: message.agent,
      message: message.end.answer.reasoning,
      message_type: 'REASONING',
      operation: 'REUSABLE FUNCTION GENERATION',
    })
    draft.streamingAiResponse = false
    draft.pendingAiResponse = false
    draft.currentAITaskMessage = null
    draft.backgroundAiResponse = true
    draft.isAiInteractionAllowed = false
    draft.messages[draft.messages.length - 1] = {
      message:
        'Function code has been generated. You can tweak the results by telling me what to change, or click Keep to apply the code to the editor.',
      context: {
        request_id: message.end?.answer?.request_id,
      },
      type: MessageType.AiMsg,
      commited: true,
    }
  }
  if (messagePayload.operation === 'gateway_condition') {
    if (messagePayload.answer) {
      draft.generatedGatewayCondition = messagePayload.answer
    }
    draft.reasoningLogs.unshift({
      agent: message.agent,
      message: messagePayload.answer?.reasoning || '',
      message_type: 'REASONING',
      operation: 'GATEWAY CONDITION GENERATION',
    })
    draft.streamingAiResponse = false
    draft.pendingAiResponse = false
    draft.pendingRouteToDeveloper = null
    draft.currentAITaskMessage = null
    draft.backgroundAiResponse = true
    draft.isAiInteractionAllowed = false
    draft.messages[draft.messages.length - 1].commited = true
  }
  if (messagePayload.operation === 'check_greeting') {
    draft.reasoningLogs.unshift({
      agent: message.agent,
      message: message.end.answer.reasoning,
      message_type: 'REASONING',
      operation: 'CHECK USER PROMPT',
    })
  }
  if (messagePayload.operation === 'process_reasoning') {
    draft.reasoningLogs.unshift({
      agent: message.agent,
      message: message.end.answer,
      message_type: 'REASONING',
      operation: 'BUSINESS RULE REASONING',
    })
  }
  if (messagePayload.operation === 'test_data') {
    generateTestData(draft, message)
    draft.reasoningLogs.unshift({
      agent: message.agent,
      message: message.end.answer.reasoning,
      message_type: 'REASONING',
      operation: 'TEST DATA GENERATION',
    })
    draft.streamingAiResponse = false
    draft.pendingAiResponse = false
    draft.pendingRouteToDeveloper = null
    draft.currentAITaskMessage = null
    draft.backgroundAiResponse = true
    draft.isAiInteractionAllowed = false
    draft.messages[draft.messages.length - 1].commited = true
  }
  if (messagePayload.operation === 'custom_component') {
    parseAnswerMessage(draft, message)
    draft.reasoningLogs.unshift({
      agent: message.agent,
      message: message.end.answer?.reasoning || '',
      message_type: 'REASONING',
      operation: 'CUSTOM COMPONENT GENERATION',
    })
    draft.streamingAiResponse = false
    draft.pendingAiResponse = false
    draft.currentAITaskMessage = null
    draft.backgroundAiResponse = true
    draft.isAiInteractionAllowed = false
    draft.messages[draft.messages.length - 1] = {
      message:
        'Custom component code has been generated. You can tweak the results by telling me what to change, or click Keep to apply the code to the editor.',
      context: {
        request_id: message.end?.answer?.request_id,
      },
      type: MessageType.AiMsg,
      commited: true,
    }
  }
  if (messagePayload.operation === 'initial_actions') {
    draft.messages[draft.messages.length - 1].commited = true
  }
}
